import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import CardContent from '@mui/material/CardContent';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import CardActionArea from '@mui/material/CardActionArea';
import Box from '@mui/material/Box';
import {
  Icon,
  SvgBookClose2Bold,
  TextV2,
} from '@insights-ltd/design-library/components';
import { InviteeResponse } from 'api';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { styled } from '@mui/material';
import { sortChapters } from 'utils/mappers/sorting';

const summariseChapters = (
  invitees: InviteeResponse[],
): Array<[string, number]> =>
  Object.entries(
    invitees.reduce((acc: any, curr) => {
      const a = { ...acc };
      curr.chapters.forEach((chapter) => {
        if (a[chapter.name]) {
          a[chapter.name] += 1;
        } else {
          a[chapter.name] = 1;
        }
      });
      return a;
    }, {}),
  );

type ChapterSummaryProps = {
  invitees: InviteeResponse[];
};

const StyledP = styled(TextV2)({
  textAlign: 'right',
});

const ChapterSummary = ({ invitees }: ChapterSummaryProps) => {
  const { t } = useTranslation();

  if (invitees.length === 0) {
    return (
      <TextV2 variant="bodyBold" color="textSecondary">
        {t('ui.event-management.events.summary.add-learner-for-chapters')}
      </TextV2>
    );
  }

  return (
    <Grid container spacing={1}>
      {summariseChapters(invitees)
        .sort(([chapterA], [chapterB]) => sortChapters(chapterA, chapterB))
        .map(([chapter, learners]) => {
          return (
            <Grid
              key={`chapter-${chapter}`}
              container
              size={12}
              sx={{
                justifyContent: 'space-between',
              }}
            >
              <Grid>
                <TextV2 variant="bodyBold" color="textSecondary">
                  {t(
                    `ui.event-management.events.assign-chapters.${chapter
                      .replace('_', '-')
                      .toLowerCase()}`,
                  )}
                </TextV2>
              </Grid>
              <Grid id={`learners-${chapter}`}>
                <StyledP variant="bodyBold" color="primary">
                  {t(
                    'ui.event-management.events.overview.summary.no-of-learners',
                    {
                      count: learners,
                    },
                  )}
                </StyledP>
              </Grid>
            </Grid>
          );
        })}
    </Grid>
  );
};

type AssignChaptersCardProps = {
  disabled: boolean;
  invitees: InviteeResponse[];
};

const AssignChaptersCard = ({
  invitees,
  disabled,
}: AssignChaptersCardProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const disabledCard = disabled || invitees.length === 0;

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        height: '100%',
        border: disabledCard ? '0' : `1px solid ${theme.palette.grey[500]}`,
        borderBottom: disabledCard
          ? 'undefined'
          : `0.2em solid ${theme.palette.primary.main}`,
        opacity: disabledCard ? '0.5' : 'undefined',
      })}
    >
      <CardActionArea
        disabled={disabledCard}
        onClick={() => navigate(`${pathname}/assign-chapters`)}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          width: '100%',
          height: '100%',
          fontFamily: 'inherit',
        }}
      >
        <CardContent sx={{ width: '100%' }}>
          <Icon
            icon={<SvgBookClose2Bold />}
            iconColour="orange"
            iconShape="square"
          />
          <Box
            sx={{
              mb: (theme) => theme.spacing(spacingSizeMap.S),
              mt: (theme) => theme.spacing(spacingSizeMap.S),
            }}
          >
            <TextV2
              variant="h3"
              color={disabledCard ? 'textPrimary' : 'primary'}
            >
              {t('ui.event-management.events.summary.assign-chapters')}
            </TextV2>
          </Box>
          <ChapterSummary invitees={invitees} />
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default AssignChaptersCard;
